import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, ArrowUp, ArrowDown, Wallet, Trash2, Loader2, X,
  TrendingUp, TrendingDown, Filter,
} from 'lucide-react';
import { extractErrorMessage } from '../../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PARTNERS = [
  { k: 'oussama', label: 'Oussama', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { k: 'rachid', label: 'Rachid', color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/30' },
  { k: 'commune', label: 'Commune', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
];

const PAYMENT_METHODS = [
  { k: 'paypal', label: 'PayPal' },
  { k: 'virement_perso', label: 'Virement personnel' },
  { k: 'virement_societe', label: 'Virement société' },
  { k: 'cash', label: 'Cash / Espèces' },
  { k: 'card', label: 'Carte bancaire' },
  { k: 'other', label: 'Autre' },
];

const EXPENSE_CATEGORIES = [
  { k: 'supplier', label: 'Paiement fournisseur' },
  { k: 'salary', label: 'Salaire / Employé' },
  { k: 'rent_charges', label: 'Loyer / Charges' },
  { k: 'misc', label: 'Frais divers' },
];

const fmtEuro = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const TreasuryTab = () => {
  const [balance, setBalance] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('income'); // 'income' | 'expense'

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [bal, list] = await Promise.all([
        axios.get(`${API_URL}/api/admin/treasury/balance`),
        axios.get(`${API_URL}/api/admin/treasury/entries`),
      ]);
      setBalance(bal.data);
      setEntries(list.data || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur chargement trésorerie'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => entries.filter((e) => {
    if (filterPartner !== 'all' && e.partner !== filterPartner) return false;
    if (filterType !== 'all' && e.type !== filterType) return false;
    return true;
  }), [entries, filterPartner, filterType]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/treasury/entries/${id}`);
      toast.success('Entrée supprimée');
      reload();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    }
  };

  const openForm = (type) => { setFormType(type); setShowForm(true); };

  return (
    <div className="space-y-6" data-testid="treasury-tab">
      {/* Balance cards by partner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PARTNERS.map((p) => {
          const b = balance?.by_partner?.[p.k] || { income: 0, expense: 0, net: 0 };
          return (
            <div
              key={p.k}
              className={`border ${p.border} ${p.bg} p-4`}
              data-testid={`treasury-balance-${p.k}`}
            >
              <div className="flex items-center justify-between mb-3">
                <p className={`text-xs uppercase tracking-widest ${p.color}`}>{p.label}</p>
                <Wallet className={`w-4 h-4 ${p.color}`} />
              </div>
              <p className={`text-3xl font-light ${b.net >= 0 ? 'text-white' : 'text-red-400'}`}
                 style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {fmtEuro(b.net)}
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="text-emerald-400 flex items-center gap-1">
                  <ArrowUp className="w-3 h-3" /> {fmtEuro(b.income)}
                </span>
                <span className="text-rose-400 flex items-center gap-1">
                  <ArrowDown className="w-3 h-3" /> {fmtEuro(b.expense)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Grand total */}
      {balance?.grand_total && (
        <div className="border border-white/10 bg-black/60 p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Solde global société</p>
            <p className={`text-2xl ${balance.grand_total.net >= 0 ? 'text-white' : 'text-red-400'}`}
               style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {fmtEuro(balance.grand_total.net)}
            </p>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="text-emerald-400 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" /> Encaissé {fmtEuro(balance.grand_total.income)}
            </span>
            <span className="text-rose-400 flex items-center gap-1">
              <TrendingDown className="w-3.5 h-3.5" /> Dépensé {fmtEuro(balance.grand_total.expense)}
            </span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => openForm('income')}
          data-testid="treasury-add-income"
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 text-xs uppercase tracking-wider"
        >
          <Plus className="w-3.5 h-3.5" /> Encaissement
        </button>
        <button
          type="button"
          onClick={() => openForm('expense')}
          data-testid="treasury-add-expense"
          className="flex items-center gap-2 bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 text-xs uppercase tracking-wider"
        >
          <Plus className="w-3.5 h-3.5" /> Dépense
        </button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-zinc-500" />
          <select
            value={filterPartner}
            onChange={(e) => setFilterPartner(e.target.value)}
            data-testid="treasury-filter-partner"
            className="bg-black border border-white/10 text-zinc-300 px-2 py-1.5 text-xs"
          >
            <option value="all">Tous associés</option>
            {PARTNERS.map((p) => <option key={p.k} value={p.k}>{p.label}</option>)}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            data-testid="treasury-filter-type"
            className="bg-black border border-white/10 text-zinc-300 px-2 py-1.5 text-xs"
          >
            <option value="all">Tous types</option>
            <option value="income">Encaissements</option>
            <option value="expense">Dépenses</option>
          </select>
        </div>
      </div>

      {/* Entries table */}
      {loading ? (
        <p className="text-zinc-500 text-sm py-8 text-center">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center" data-testid="treasury-empty">
          Aucune entrée enregistrée pour le moment.
        </p>
      ) : (
        <div className="overflow-x-auto border border-white/5">
          <table className="w-full text-sm" data-testid="treasury-entries-table">
            <thead>
              <tr className="text-zinc-500 text-[10px] uppercase tracking-widest bg-white/5">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Associé</th>
                <th className="text-left p-3">Motif</th>
                <th className="text-left p-3">Méthode / Catégorie</th>
                <th className="text-right p-3">Montant</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const partner = PARTNERS.find((p) => p.k === e.partner) || PARTNERS[2];
                const isIncome = e.type === 'income';
                const meta = isIncome
                  ? PAYMENT_METHODS.find((m) => m.k === e.payment_method)?.label || e.payment_method || '—'
                  : EXPENSE_CATEGORIES.find((c) => c.k === e.expense_category)?.label || e.expense_category || '—';
                return (
                  <tr key={e.id}
                      className="border-t border-white/5 hover:bg-white/[0.02]"
                      data-testid={`treasury-row-${e.id}`}>
                    <td className="p-3 text-zinc-400 whitespace-nowrap">{fmtDate(e.occurred_at || e.created_at)}</td>
                    <td className="p-3">
                      {isIncome ? (
                        <span className="text-emerald-400 flex items-center gap-1 text-xs">
                          <ArrowUp className="w-3 h-3" /> Entrée
                        </span>
                      ) : (
                        <span className="text-rose-400 flex items-center gap-1 text-xs">
                          <ArrowDown className="w-3 h-3" /> Sortie
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`text-xs ${partner.color}`}>{partner.label}</span>
                    </td>
                    <td className="p-3 text-white break-words max-w-xs">{e.motif}</td>
                    <td className="p-3 text-zinc-400 text-xs">{meta}</td>
                    <td className={`p-3 text-right font-medium whitespace-nowrap ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {isIncome ? '+' : '−'} {fmtEuro(e.amount)}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(e.id)}
                        title="Supprimer"
                        data-testid={`treasury-delete-${e.id}`}
                        className="text-zinc-500 hover:text-rose-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <TreasuryEntryForm
          type={formType}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); reload(); }}
        />
      )}
    </div>
  );
};

export default TreasuryTab;

// =========================================================================

const TreasuryEntryForm = ({ type, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [partner, setPartner] = useState('oussama');
  const [method, setMethod] = useState('cash');
  const [category, setCategory] = useState('supplier');
  const [motif, setMotif] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [saving, setSaving] = useState(false);

  const isIncome = type === 'income';

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Montant invalide'); return; }
    if (!motif.trim()) { toast.error('Motif obligatoire'); return; }
    const payload = {
      type,
      amount: amt,
      partner,
      motif: motif.trim(),
    };
    if (isIncome) payload.payment_method = method;
    else payload.expense_category = category;
    if (occurredAt) payload.occurred_at = new Date(occurredAt).toISOString();

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/treasury/entries`, payload);
      toast.success(isIncome ? 'Encaissement enregistré' : 'Dépense enregistrée');
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  const fld = "w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none";
  const lbl = "text-zinc-500 text-[10px] uppercase tracking-wider block mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="treasury-form-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-2xl font-light ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {isIncome ? 'Nouvel encaissement' : 'Nouvelle dépense'}
          </h2>
          <button type="button" onClick={onClose}
            className="text-zinc-500 hover:text-white"
            data-testid="treasury-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className={lbl}>Montant € *</label>
            <input type="number" step="0.01" min="0" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="treasury-form-amount" className={fld} autoFocus />
          </div>
          <div>
            <label className={lbl}>Associé qui reçoit / paie *</label>
            <div className="grid grid-cols-3 gap-2">
              {PARTNERS.map((p) => (
                <button
                  key={p.k}
                  type="button"
                  onClick={() => setPartner(p.k)}
                  data-testid={`treasury-form-partner-${p.k}`}
                  className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
                    partner === p.k ? `${p.border} ${p.bg} ${p.color}` : 'border-white/10 text-zinc-500 hover:border-white/30'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {isIncome ? (
            <div>
              <label className={lbl}>Méthode de paiement *</label>
              <select value={method}
                onChange={(e) => setMethod(e.target.value)}
                data-testid="treasury-form-method" className={fld}>
                {PAYMENT_METHODS.map((m) => <option key={m.k} value={m.k}>{m.label}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className={lbl}>Catégorie de dépense *</label>
              <select value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="treasury-form-category" className={fld}>
                {EXPENSE_CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className={lbl}>Motif *</label>
            <input type="text" value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder={isIncome ? 'Ex: Commande Khady Ndiaye' : 'Ex: Réassort Dubai Flower, salaire H. juin'}
              data-testid="treasury-form-motif" className={fld} />
          </div>
          <div>
            <label className={lbl}>Date</label>
            <input type="date" value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              data-testid="treasury-form-date" className={fld} />
            <p className="text-zinc-600 text-[10px] mt-1">Laissez vide pour utiliser maintenant</p>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={submit}
            disabled={saving}
            data-testid="treasury-form-submit"
            className={`flex-1 py-2.5 text-xs uppercase tracking-wider text-white disabled:opacity-40 ${
              isIncome ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-rose-600 hover:bg-rose-500'
            }`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : 'Enregistrer'}
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};
